import { useId, useState } from "react";

import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { blockStyle } from "../ui/Block";
import { SUPPORTED_IMAGE_EXTENSIONS, UNSUPPORTED_IMAGE_EXTENSIONS } from "./constants";
import { photoToDataUri, prepareSubjectPhoto } from "./preparePhoto";

/**
 * THE CONFIRMED SUBJECT PHOTO — THE ONE THAT PRINTS (2026-09-25, owner: P0).
 *
 * "There is NO way to upload or change the subject image anywhere in the website workflow" — and
 * the delivered brief told the homeowner to "Upload one and it prints here. add before sending".
 * The product instructed an action it gave nowhere to take. This is that place, on the job page's
 * review step and on the finished report, and it is the same control in both.
 *
 * NOT THE STREET VIEW SLOT. The review screen's "Reference photo (subject)" is Google Street View,
 * display only, never part of the PDF/DOCX — and it stays exactly that. This control only ever holds
 * a file the agent picked; nothing here can take the Street View image, and the caption says which
 * of the two prints.
 *
 * `onPhoto` receives a bounded JPEG data URI and may be async (the finished report saves it to the
 * server); a rejection's message is shown here, in the same place the control is.
 */
export function SubjectPhotoUpload({
  photo,
  origin,
  onPhoto,
  where,
  photoOptionsShown = false,
}: {
  /** The photo picker above lists Street View as a choice, so the "never used" sentence would
   * contradict it and is left out. */
  photoOptionsShown?: boolean;
  /** What prints now: the agent's photo, a photo read from their subject sheet, or nothing. */
  photo: string | null;
  /** How `photo` got here, in words for the agent. Ignored when `photo` is null. */
  origin: string;
  onPhoto: (dataUri: string) => void | Promise<void>;
  /** "review" before the brief is built; "report" once it exists. Only the words differ. */
  where: "review" | "report";
}) {
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const prepared = await prepareSubjectPhoto(file);
      if (!prepared.file) {
        setError(prepared.error ?? `${file.name} isn't a photo. Choose a JPEG, PNG or WebP.`);
        return;
      }
      const uri = await photoToDataUri(prepared.file);
      if (!uri) {
        setError(`This browser couldn't read ${file.name} as an image. Try saving it as a JPEG first.`);
        return;
      }
      await onPhoto(uri);
      setNote(prepared.note);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The photo could not be saved. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const accept = [...SUPPORTED_IMAGE_EXTENSIONS, ...UNSUPPORTED_IMAGE_EXTENSIONS].map((e) => "." + e).join(",");
  const prints = where === "report"
    ? "It prints on the first page of this brief, in the PDF and the Word file, and replaces what is there now. No figure changes."
    : "It prints on the first page of the brief, in the PDF and the Word file.";

  return (
    <section
      aria-labelledby={`${inputId}-h`}
      data-testid="subject-photo-upload"
      style={{ marginTop: 16, background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 10,
               padding: "14px 16px", fontFamily: typeTokens.fontUi, color: colors.textPrimary }}
    >
      <h3 id={`${inputId}-h`} style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Subject photo</h3>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginTop: 10, flexWrap: "wrap" }}>
        {photo ? (
          <img src={photo} alt="The subject photo that prints in the brief"
               style={{ width: 160, height: 110, objectFit: "cover", borderRadius: 6, border: `1px solid ${colors.border}` }} />
        ) : (
          <div style={{ width: 160, height: 110, borderRadius: 6, border: `1px dashed ${colors.border}`,
                        display: "grid", placeItems: "center", fontSize: 12, color: colors.textSecondary, textAlign: "center",
                        padding: 8, boxSizing: "border-box" }}>
            No photo yet
          </div>
        )}
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: colors.textSecondary, ...blockStyle("copy") }}>
            {photo
              ? <>Printing now: <strong style={{ color: colors.textPrimary, fontWeight: 600 }}>{origin}</strong>. </>
              : <>Nothing prints in the photo frame yet — the brief will say no photograph is included. </>}
            {prints}{photoOptionsShown ? "" : " A Street View picture is never used as this photo."}
          </p>
          <label
            htmlFor={inputId}
            className="btn"
            aria-disabled={busy}
            style={{ display: "inline-block", marginTop: 10, cursor: busy ? "progress" : "pointer" }}
          >
            {busy ? "Preparing the photo…" : photo ? "Replace the photo" : "Upload a photo"}
          </label>
          <input
            id={inputId}
            type="file"
            accept={accept}
            disabled={busy}
            aria-label={photo ? "Replace the subject photo" : "Upload a subject photo"}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, overflow: "hidden" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              void pick(f);
            }}
          />
          {note && <p style={{ margin: "8px 0 0", fontSize: 12, color: colors.textSecondary }}>{note}</p>}
          {error && <p role="alert" style={{ margin: "8px 0 0", fontSize: 12, color: colors.caution }}>{error}</p>}
        </div>
      </div>
    </section>
  );
}
